import { SetURLSearchParams } from 'react-router-dom'
import { UrlParams } from 'enums/url-params'
import { UAParser } from 'ua-parser-js'

export const isProd = (environment: string) => environment.includes('prod')
export const isNonProd = (environment: string) => !environment.includes('prod')

export const isEntitled = (
    greenfieldGroups: string[],
    entitledGroups: string[],
) => entitledGroups.some((group) => greenfieldGroups?.includes(group))

let cachedIsZebra: boolean | undefined

export function resetIsZebraCache() {
    cachedIsZebra = undefined
    localStorage.removeItem('isZebraDevice')
}

const ZEBRA_APK_PROTOCOL = 'android-app:' as const

/** Safely parse a URL string; returns null if unparseable */
function safeParseUrl(input: string | null, win?: Window): URL | null {
    if (!input) return null
    try {
        // Referrers should be absolute, but provide a base just in case.
        const base = win?.location?.origin ?? 'https://example.invalid'
        return new URL(input, base)
    } catch {
        return null
    }
}

export function isZebraDevice(win: Window): boolean {
    if (localStorage.getItem('isZebraDevice') === 'true') return true
    if (cachedIsZebra !== undefined) return cachedIsZebra
    const ref = (win?.document?.referrer ?? '').trim()
    const url = ref ? safeParseUrl(ref, win) : null
    const isZebra = !!url && url.protocol === ZEBRA_APK_PROTOCOL
    isZebra && localStorage.setItem('isZebraDevice', 'true')
    if (url) cachedIsZebra = isZebra
    return isZebra
}

type UrlParamsType = {
    params: URLSearchParams
    setParams: SetURLSearchParams
}
export const clearLocationParam = ({ params, setParams }: UrlParamsType) => {
    const locationParamExists = params.get(UrlParams.LOCATION_ID)
    if (locationParamExists) {
        params.delete(UrlParams.LOCATION_ID)
        setParams(params)
    }
}

export type DeviceSummary = {
    device: string
}

let cachedDeviceInfo: DeviceSummary | null = null

// info for logging purposes
export function getDeviceSummary(): DeviceSummary {
    if (cachedDeviceInfo) return cachedDeviceInfo

    const parser = new UAParser()
    const result = parser.getResult()

    const ua = navigator.userAgent
    const os = (result.os.name ?? '').toLowerCase()
    const isZebra = isZebraDevice(window)
    const isAndroid = os.startsWith('android') && !isZebra
    const isiPhone = /iPhone/.test(ua)
    const isiPad = /iPad/.test(ua) || result.device.model === 'iPad'
    const isiOS = os.startsWith('ios')

    let device = 'Unknown'

    if (isZebra) {
        device = 'Zebra'
    } else if (isiPhone) {
        device = 'iPhone'
    } else if (isAndroid) {
        device = 'Android'
    } else if (isiPad) {
        device = 'iPad'
    } else if (isiOS) {
        device = 'iOS Device'
    } else if (result.device.model) {
        device = result.device.model
    } else if (result.device.type) {
        device = result.device.type
    }
    cachedDeviceInfo = { device }

    return cachedDeviceInfo
}

export function resetDeviceCache() {
    cachedDeviceInfo = null
}

/**
 * Determines whether the current device is a personal device.
 * Zebra devices are classified as zebra devices;
 * all other devices (iPhone, iPad, Android, etc.) are personal.
 */
export function isPersonalDevice(): boolean {
    return !isZebraDevice(window)
}

export type DeviceCategory = 'personal' | 'zebra'

/**
 * Returns the device category for Firebase Remote Config targeting.
 * 'zebra' = Zebra device, 'personal' = everything else.
 */
export function getDeviceCategory(): DeviceCategory {
    return isPersonalDevice() ? 'personal' : 'zebra'
}

export function decodeHtmlEntities(html: string): string {
    const textarea = document.createElement('textarea')
    textarea.innerHTML = html
    return textarea.value
}
